import type { EditorAnalyticsAPI } from '@atlaskit/editor-common/analytics/api';
import {
	ACTION,
	ACTION_SUBJECT,
	ACTION_SUBJECT_ID,
	EVENT_TYPE,
} from '@atlaskit/editor-common/analytics/types/enums';
import {
	changeImageAlignment,
	changeImageAlignmentNext,
	toggleBlockMark,
	toggleBlockMarkNext,
} from '@atlaskit/editor-common/commands';
import type { Command, CommandDispatch } from '@atlaskit/editor-common/types/command';
import type { EditorCommand } from '@atlaskit/editor-common/types/editor-command';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import { withAnalytics } from '@atlaskit/editor-common/withAnalytics';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import type { EditorState, Transaction } from '@atlaskit/editor-prosemirror/state';
import { Selection } from '@atlaskit/editor-prosemirror/state';
import { CellSelection } from '@atlaskit/editor-tables/cell-selection';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';

import type { AlignmentPlugin, InputMethod } from '../alignmentPluginType';
import type { AlignmentState } from '../pm-plugins/types';

/**
 * Iterates over the commands one after the other,
 * passes the tr through and dispatches the cumulated transaction
 */
function cascadeCommands(cmds: Command[]): Command {
	return (state: EditorState, dispatch?: CommandDispatch) => {
		const { tr: baseTr } = state;
		let shouldDispatch = false;

		const onDispatchAction = (tr: Transaction) => {
			const selectionJSON = tr.selection.toJSON();
			baseTr.setSelection(Selection.fromJSON(baseTr.doc, selectionJSON));
			tr.steps.forEach((st) => {
				baseTr.step(st);
			});
			shouldDispatch = true;
		};

		cmds.forEach((cmd) => cmd(state, onDispatchAction));

		if (dispatch && shouldDispatch) {
			dispatch(baseTr);
			return true;
		}

		return false;
	};
}

export const isAlignable =
	(align?: AlignmentState): Command =>
	(state, dispatch) => {
		const {
			nodes: { paragraph, heading },
			marks: { alignment },
		} = state.schema;
		if (
			!dispatch &&
			state.selection instanceof CellSelection &&
			isExperimentEnabled('platform_editor_improve_inp_batch_2')
		) {
			if (!align) {
				return false;
			}

			// toggleBlockMark returns the result of the last cell, rather than
			// combining results. Preserve that behavior without building a transaction.
			// CellSelection moves the head range first, leaving the rest in document
			// order. Compare both ends to find the final cell visited by forEachCell.
			const { ranges } = state.selection;
			const firstRange = ranges[0];
			const lastRange = ranges[ranges.length - 1];
			const lastCellRange = firstRange.$from.pos > lastRange.$from.pos ? firstRange : lastRange;
			const lastCell = lastCellRange.$from.parent;
			const hasAlignableBlock = (parent: PMNode): boolean => {
				for (let index = 0; index < parent.childCount; index++) {
					const node = parent.child(index);
					if (!node.isBlock) {
						continue;
					}
					if (
						(node.type === paragraph || node.type === heading) &&
						parent.type.allowsMarkType(alignment)
					) {
						return true;
					}
					if (hasAlignableBlock(node)) {
						return true;
					}
				}
				return false;
			};
			return hasAlignableBlock(lastCell);
		}
		return toggleBlockMark(
			alignment,
			() => (!align ? undefined : align === 'start' ? false : { align }),
			[paragraph, heading],
		)(state, dispatch);
	};

const changeBlockAlignmentWithAnalytics =
	(
		editorAnalyticsApi?: EditorAnalyticsAPI,
		align?: AlignmentState,
		inputMethod?: InputMethod,
	): Command =>
	(state, dispatch) => {
		const {
			nodes: { paragraph, heading },
			marks: { alignment },
		} = state.schema;

		return withAnalytics(editorAnalyticsApi, {
			eventType: EVENT_TYPE.TRACK,
			actionSubject: ACTION_SUBJECT.ALIGNMENT,
			action: ACTION.UPDATED,
			actionSubjectId: ACTION_SUBJECT_ID.TEXT,
			attributes: {
				alignmentType: align,
				inputMethod,
			},
		})(
			toggleBlockMark(
				alignment,
				() => (!align ? undefined : align === 'start' ? false : { align }),
				[paragraph, heading],
			),
		)(state, dispatch);
	};

const changeBlockAlignmentWithAnalyticsTr =
	(
		api: ExtractInjectionAPI<AlignmentPlugin> | undefined,
		align?: AlignmentState,
		inputMethod?: InputMethod,
	): EditorCommand =>
	({ tr }) => {
		const {
			nodes: { paragraph, heading },
			marks: { alignment },
		} = tr.doc.type.schema;

		const alignmentApplied = toggleBlockMarkNext(
			alignment,
			() => (!align ? undefined : align === 'start' ? false : { align }),
			[paragraph, heading],
		)(tr);

		if (alignmentApplied) {
			api?.analytics?.actions.attachAnalyticsEvent({
				eventType: EVENT_TYPE.TRACK,
				actionSubject: ACTION_SUBJECT.ALIGNMENT,
				action: ACTION.UPDATED,
				actionSubjectId: ACTION_SUBJECT_ID.TEXT,
				attributes: {
					alignmentType: align,
					inputMethod,
				},
			})(tr);
		}

		return tr;
	};

const changeImageAlignmentWithAnalytics =
	(
		editorAnalyticsApi?: EditorAnalyticsAPI,
		align?: AlignmentState,
		inputMethod?: InputMethod,
	): Command =>
	(state, dispatch) => {
		return withAnalytics(editorAnalyticsApi, {
			eventType: EVENT_TYPE.TRACK,
			actionSubject: ACTION_SUBJECT.ALIGNMENT,
			action: ACTION.UPDATED,
			actionSubjectId: ACTION_SUBJECT_ID.MEDIA_SINGLE,
			attributes: {
				alignmentType: align,
				inputMethod,
			},
		})(changeImageAlignment(align))(state, dispatch);
	};

const changeImageAlignmentWithAnalyticsTr =
	(
		api?: ExtractInjectionAPI<AlignmentPlugin>,
		align?: AlignmentState,
		inputMethod?: InputMethod,
	): EditorCommand =>
	({ tr }) => {
		const alignmentApplied = changeImageAlignmentNext(align)(tr);

		if (alignmentApplied) {
			api?.analytics?.actions.attachAnalyticsEvent({
				eventType: EVENT_TYPE.TRACK,
				actionSubject: ACTION_SUBJECT.ALIGNMENT,
				action: ACTION.UPDATED,
				actionSubjectId: ACTION_SUBJECT_ID.MEDIA_SINGLE,
				attributes: {
					alignmentType: align,
					inputMethod,
				},
			})(tr);
		}

		return tr;
	};

// eslint-disable-next-line @repo/internal/deprecations/deprecation-ticket-required
/**
 * Command-based alignment used by the legacy primary/floating toolbars and alignment keymap.
 * The new toolbar uses changeAlignmentTr.
 *
 * @deprecated use changeAlignmentTr instead
 */
export const changeAlignment =
	(
		align?: AlignmentState,
		api?: ExtractInjectionAPI<AlignmentPlugin>,
		inputMethod?: InputMethod,
	): Command =>
	(state, dispatch) => {
		return cascadeCommands([
			changeImageAlignmentWithAnalytics(api?.analytics?.actions, align, inputMethod),
			changeBlockAlignmentWithAnalytics(api?.analytics?.actions, align, inputMethod),
		])(state, dispatch);
	};

export const changeAlignmentTr =
	(
		api: ExtractInjectionAPI<AlignmentPlugin> | undefined,
		align?: AlignmentState,
		inputMethod?: InputMethod,
	): EditorCommand =>
	({ tr }) => {
		changeBlockAlignmentWithAnalyticsTr(api, align, inputMethod)({ tr });
		changeImageAlignmentWithAnalyticsTr(api, align, inputMethod)({ tr });
		return tr;
	};
